import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { RootStore } from 'store/types';
import { Breadcrumbs, IBreadcrumbsLink } from '../../../ui-toolkit';
import { eApplicationActions, useApplicationContext } from 'state';
import { OrderHeader } from './OrderHeader';
import { StickyOrderHeader } from './StickyOrderHeader';
import { putResetOrderByIDAsyncAction } from 'store/order/resetOrder/resetOrderSlice';
import { loadOrderAsyncAction } from 'store/order/batchedDispatches';
import { useAppDispatch } from 'store/store';
import { combineFetchStatus } from 'types/rest/IFetchStatus';
import { cancelOrderAsync, updateOrderCancellationRequestAsync } from 'store/order/orderCancellationRequest';
import { CancelOrderModal } from 'components/order/overview/OrderCancel/CancelOrderModal';
import { UpdateCancellationRequestDialog } from 'components/order/overview/OrderCancel/UpdateCancellationRequestDialog';
import { getDefaultHomeLinkForUserType } from 'components/accessControl/getDefaultHomeLinkForUserType';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { eOrderGroupCategory } from '../../../ui-toolkit/enums/eOrderGroupCategory';
import { MobilityHeader } from './mobility/MobilityHeader';
import { useListTransactionTypesQuery } from '../../../API/mobilityGroups/MobilityGroupApi';
import { useListProgramTypesQuery } from 'API/mobilityGroups/MobilityGroupApi';
import { useDocumentTitleEffect } from '../../../utilities/hooks/useDocumentTitleEffect';
import { ErrorBoundaryTW } from '../../../tailwindComponents/errorBoundary/ErrorBoundaryTW';
import { QuickTaskContainer } from './quickOptions/QuickTaskContainer';
import { QuickCommentContainer } from './quickOptions/QuickCommentContainer';
import { useGetUserType } from 'components/accessControl/hooks/useGetUserType';
import { eCancellationRequestStatus } from '../../../ui-toolkit/orders/interfaces/eCancellationRequestStatus';
import { notEmpty } from '../../../ui-toolkit/utilities/notEmpty';

export interface IOrderHeaderContainerProps {}

export const OrderHeaderContainer: React.FC<IOrderHeaderContainerProps> = () => {
  const { data: order } = useSelectCurrentOrder();
  const orderId = order.transaction.id!;
  const [showUpdateCancellationRequestDialog, setShowUpdateCancellationRequestDialog] = useState(false);
  const dispatch = useAppDispatch();
  const [, dispatchContext] = useApplicationContext();
  const { tab, title, lastBreadcrumb } = useSelector((rootStore: RootStore) => rootStore.order.orderNavigationSlice);

  useDocumentTitleEffect(title ?? `${tab || 'Order Overview'}: File Number ${order.transaction.file_number} - Spruce`);

  const userType = useGetUserType();
  const homeLink = getDefaultHomeLinkForUserType(userType);

  const { data: transactionTypes = [] } = useListTransactionTypesQuery();

  const { resetOrderSlice } = useSelector(({ order: { resetOrderSlice } }: RootStore) => ({
    resetOrderSlice,
  }));
  const { cancellationRequest, updateCancellationRequestFetchStatus, updateCancellationRequestError } = useSelector(
    ({ order }: RootStore) => order.orderCancellationRequest
  );
  const combinedFetchStatus = combineFetchStatus([resetOrderSlice.fetchStatus]);

  const { data: programTypes = [] } = useListProgramTypesQuery();

  const reopenOrder = async () => {
    await dispatch(putResetOrderByIDAsyncAction({ orderId }));
    await dispatch(loadOrderAsyncAction(orderId));
  };

  const closeDialog = () =>
    dispatchContext({ type: eApplicationActions.ChangeValues, payload: { showCancelConfirmDialog: false } });

  const onCancelOrder = async (reason?: string) => {
    try {
      await dispatch(cancelOrderAsync(orderId, reason));
      await dispatch(loadOrderAsyncAction(orderId));
    } catch (e: any) {}
  };
  const onCancellationRequestUpdate = async (type: eCancellationRequestStatus, reason?: string) => {
    if (cancellationRequest) {
      try {
        await dispatch(updateOrderCancellationRequestAsync(orderId!, cancellationRequest.id, type, reason));
        setShowUpdateCancellationRequestDialog(false);
        await dispatch(loadOrderAsyncAction(orderId));
      } catch (e: any) {}
    }
  };

  const breadCrumbLinks: IBreadcrumbsLink[] = [
    {
      to: ClientUrlFactory().urlFor.adminOrders(),
      text: 'Orders',
    },
    {
      to: ClientUrlFactory().urlFor.orderStatus(order.transaction.id!),
      text: order.transaction.file_number!,
    },
    lastBreadcrumb
      ? {
          text: lastBreadcrumb,
        }
      : undefined,
  ].filter(notEmpty);

  return (
    <ErrorBoundaryTW>
      <CancelOrderModal
        order={order}
        onCancelOrder={onCancelOrder}
        closeDialog={closeDialog}
        cancellationFetchStatus={updateCancellationRequestFetchStatus}
        cancellationError={updateCancellationRequestError}
      />
      {showUpdateCancellationRequestDialog && cancellationRequest && (
        <UpdateCancellationRequestDialog
          onClose={() => setShowUpdateCancellationRequestDialog(false)}
          onUpdate={onCancellationRequestUpdate}
          fetchStatus={updateCancellationRequestFetchStatus}
          fetchError={updateCancellationRequestError}
          cancellationRequest={cancellationRequest}
        />
      )}
      <StickyOrderHeader order={order} activeTab={tab} />
      <Breadcrumbs homeLink={homeLink} links={breadCrumbLinks} />
      <ErrorBoundaryTW>
        {order.transaction.order_group_category === eOrderGroupCategory.Mobility &&
          !!order.transaction.mobility_group && (
            <MobilityHeader
              currentOrderId={orderId}
              groupId={order.transaction.mobility_group.id}
              mobilityAdditionFeatureEnabled={false}
              transactionTypes={transactionTypes}
              programTypes={programTypes}
            />
          )}
      </ErrorBoundaryTW>
      <OrderHeader
        order={order}
        canCancelOrder={!cancellationRequest || cancellationRequest.status !== eCancellationRequestStatus.Pending}
        resetOrder={reopenOrder}
        fetchStatus={combinedFetchStatus}
      />
      <QuickTaskContainer />
      <QuickCommentContainer />
    </ErrorBoundaryTW>
  );
};
